'use strict';

/**
 * PdfCanvas — renders one page of a PDF onto a canvas via pdfjs-dist.
 *
 * NOTE on per-page extraction: in the Markup Studio, `pdfUrl` is a
 * server-extracted SINGLE-PAGE micro-PDF (so the browser never holds a
 * 700 MB construction set in memory). We always render page 1 of whatever
 * URL we're given. The `pageNumber` prop is kept for backward compat with
 * callers that pass a multi-page doc, but the studio relies on the URL
 * changing per page navigation.
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as pdfjsLib from 'pdfjs-dist';

// Worker file is copied to client/public during build (see migration notes).
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

// Per-document cache (one entry per pdfUrl) so swapping pages doesn't reload
// the whole PDF. We keep a module-level WeakMap-ish object since pdfjs-dist
// returns plain objects.
const docCache = new Map();
async function loadDoc(pdfUrl) {
  if (docCache.has(pdfUrl)) return docCache.get(pdfUrl);
  // /pdf is now a same-origin Range proxy through Node (not a redirect to
  // S3), so cookies must flow → withCredentials: true. The proxy reads the
  // browser's Range header, forwards to S3, streams the partial body back.
  //
  // PDF.js uses Range requests automatically as long as the response carries
  // Accept-Ranges: bytes (it does). disableAutoFetch prevents PDF.js from
  // opportunistically downloading the entire 700 MB+ in the background once
  // it confirms range support — only the visible page is fetched.
  const loadingTask = pdfjsLib.getDocument({
    url: pdfUrl,
    withCredentials: true,
    disableAutoFetch: true,
    disableStream: false,
    disableRange: false,
  });
  const doc = await loadingTask.promise;
  docCache.set(pdfUrl, doc);
  return doc;
}

export default function PdfCanvas({
  pdfUrl,
  pageNumber = 1,
  scale = 1,
  fitToWidth = false,    // overrides `scale`: auto-sizes so page width fits
                         // the container (large drawings ~3000pt wide
                         // otherwise overflow on a 1100px viewport)
  containerRef,          // ref to the scroll container; we measure its width
                         // when fitToWidth is on
  displayZoom = 1,       // Camera zoom (scale). Outer wrapper stays at
                         // fit-to-width SIZE; only the content inside
                         // scales. >1 shows a smaller portion at higher
                         // detail; <1 shows more.
  panX = 0,              // Camera X position in canvas-pixel space
                         // (i.e. pre-zoom pixel of the canvas's top-left
                         // showing at the viewport's top-left).
  panY = 0,
  onReady,
  onClickPdfCoords,
  children,
}) {
  const canvasRef = useRef(null);
  const wrapperRef = useRef(null);
  const renderTaskRef = useRef(null);
  const fitScaleRef = useRef(1);  // set inside render effect; used for layout math
  const [error, setError] = useState(null);
  // Two sizes — kept separate so transient mismatches during a zoom
  // re-render don't shrink/grow the outer viewport.
  //   size:        current RASTERIZED canvas dimensions (naturalPdf × effectiveScale).
  //                Changes on every zoom step.
  //   viewportSize: STABLE fit-to-window dimensions (naturalPdf × fitScale),
  //                only changes when the container is resized or the page
  //                is swapped. Used for the outer wrapper layout.
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [viewportSize, setViewportSize] = useState({ width: 0, height: 0 });
  const [pageViewport, setPageViewport] = useState(null);

  // Render a single page. Cancels any in-flight render first so quick page
  // flips don't overlap.
  useEffect(() => {
    if (!pdfUrl) return undefined;
    let cancelled = false;

    (async () => {
      try {
        const doc = await loadDoc(pdfUrl);
        if (cancelled) return;

        // pdfUrl is a single-page micro-PDF (see file header). If a caller
        // ever hands us a multi-page doc, fall through to pageNumber.
        const safePage = doc.numPages === 1
          ? 1
          : Math.max(1, Math.min(pageNumber, doc.numPages));
        const page = await doc.getPage(safePage);

        // Resolve effective rasterization scale.
        //   fitScale = scale at which canvas exactly fills the viewport
        //              (computed from container width when fitToWidth)
        //   effectiveScale = fitScale × displayZoom — what we actually
        //              hand to PDF.js. Re-rasterizing at this scale on
        //              every zoom change keeps the image sharp at any
        //              zoom level (Preview / Adobe behavior) instead of
        //              CSS-scaling a fixed-resolution raster.
        const naturalViewport = page.getViewport({ scale: 1 });
        let fitScale = scale;
        if (fitToWidth) {
          const container = containerRef?.current;
          const containerWidth = container ? container.clientWidth : window.innerWidth;
          const usable = Math.max(200, containerWidth - 32);
          fitScale = usable / naturalViewport.width;
        }
        const effectiveScale = fitScale * displayZoom;
        fitScaleRef.current = fitScale; // remember for layout math below

        const viewport = page.getViewport({ scale: effectiveScale });

        if (renderTaskRef.current) {
          try { renderTaskRef.current.cancel(); } catch (_) { /* ignore */ }
          renderTaskRef.current = null;
        }

        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        const dpr = window.devicePixelRatio || 1;

        canvas.width = Math.floor(viewport.width * dpr);
        canvas.height = Math.floor(viewport.height * dpr);
        canvas.style.width = `${viewport.width}px`;
        canvas.style.height = `${viewport.height}px`;

        const renderContext = {
          canvasContext: ctx,
          viewport,
          transform: dpr === 1 ? null : [dpr, 0, 0, dpr, 0, 0],
        };
        const task = page.render(renderContext);
        renderTaskRef.current = task;

        await task.promise;
        if (cancelled) return;

        renderTaskRef.current = null;
        setSize({ width: viewport.width, height: viewport.height });
        // Update viewport (fit-size) box ONLY when fitScale changes. We
        // detect that by comparing to the natural page width × current
        // fitScale — that product is invariant to displayZoom. This
        // prevents the outer wrapper from momentarily snapping smaller
        // when a re-render fires for a zoom change.
        const fitW = naturalViewport.width * fitScale;
        const fitH = naturalViewport.height * fitScale;
        setViewportSize((prev) => (
          Math.abs(prev.width - fitW) > 0.5 || Math.abs(prev.height - fitH) > 0.5
            ? { width: fitW, height: fitH }
            : prev
        ));
        setPageViewport(viewport);
        setError(null);
        if (onReady) {
          onReady({
            pageCount: doc.numPages,
            pageRect: { width: viewport.width, height: viewport.height },
            scale: effectiveScale,
          });
        }
      } catch (err) {
        if (cancelled || err?.name === 'RenderingCancelledException') return;
        console.error('[PdfCanvas] render failed:', err);
        setError(err.message || 'Failed to render PDF page');
      }
    })();

    return () => {
      cancelled = true;
      if (renderTaskRef.current) {
        try { renderTaskRef.current.cancel(); } catch (_) { /* ignore */ }
        renderTaskRef.current = null;
      }
    };
  }, [pdfUrl, pageNumber, scale, fitToWidth, displayZoom, containerRef, onReady]);

  // Convert click coords (pixels in the canvas frame) to PDF coordinates,
  // then re-orient to TOP-LEFT origin / y-down (PyMuPDF / fitz convention)
  // — that's what annotate_drawing.py expects. Engineering drawings often
  // have non-(0,0) viewBox origins (e.g. [-1512,-1080,1512,1080]) so we
  // must subtract viewBox[0] and use viewBox[3] for the y flip, NOT the
  // full page height. See MarkupOverlay.js for the matching math.
  const handleClick = useCallback(
    (evt) => {
      if (!onClickPdfCoords || !pageViewport) return;
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      // Canvas is rendered at PDF.js's effectiveScale natively.
      // fitz coords (visual top-left origin, used by annotate_drawing.py)
      // = canvas-pixel coords / viewport.scale. This is rotation- and
      // viewBox-origin-independent (see matching math in MarkupOverlay).
      const px = evt.clientX - rect.left;
      const py = evt.clientY - rect.top;
      const fitzX = px / pageViewport.scale;
      const fitzY = py / pageViewport.scale;
      onClickPdfCoords({ x: fitzX, y: fitzY, screenX: px, screenY: py, viewport: pageViewport });
    },
    [onClickPdfCoords, pageViewport]
  );

  // Camera/viewport model with NATIVE-resolution re-rasterization:
  //   OUTER: fixed at fit-size (naturalPdfW × fitScale). Never changes
  //          when the user zooms. overflow:hidden clips the inner to
  //          show only a portion of the higher-res canvas.
  //   INNER: absolute, sized to the freshly rasterized canvas
  //          (size.width × size.height = naturalPdfW × effectiveScale).
  //          ONLY a translate is applied — no CSS scale — so every pixel
  //          you see was rendered by PDF.js at the current effective
  //          scale. Sharp at every zoom (Preview / Adobe behavior).
  //
  // Layout math:
  //   fitSize = renderSize / displayZoom  (the original fit-to-window box)
  //   translate = (-panX * fitScale * displayZoom, -panY * ...) — panX/panY
  //               are in canvas-PIXEL-AT-FIT units so they're invariant
  //               to zoom changes (camera doesn't jump on zoom).
  //
  // Click + drag math: getBoundingClientRect on canvas/SVG returns the
  // native rendered pixel rect (no CSS scale active), so the existing
  // PDF.js convertToPdfPoint(canvas-px) flow is correct without any
  // additional division.
  // Outer wrapper uses the STABLE viewport size, not size/displayZoom.
  // Otherwise, during a zoom change, size lags one render behind the new
  // displayZoom and the outer briefly resizes to the wrong value.
  // panX/panY are stored in FIT-CANVAS-PIXEL units (invariant to zoom
  // changes — the camera doesn't jump when the user zooms). To translate
  // the higher-resolution rendered canvas, multiply by displayZoom: at
  // zoom 2× one fit-pixel maps to 2 rendered pixels.
  const tx = -panX * displayZoom;
  const ty = -panY * displayZoom;
  return (
    <div
      ref={wrapperRef}
      className="relative inline-block overflow-hidden"
      style={{
        width: viewportSize.width || 'auto',
        height: viewportSize.height || 'auto',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: size.width || 'auto',
          height: size.height || 'auto',
          transform: `translate(${tx}px, ${ty}px)`,
          transformOrigin: '0 0',
          willChange: 'transform',
        }}
      >
        <canvas
          ref={canvasRef}
          onClick={handleClick}
          className="block bg-white shadow-sm"
          style={{ cursor: onClickPdfCoords ? 'crosshair' : 'default' }}
        />
        {/* Overlay layer (SVG) is rendered into the same box at css pixels */}
        {size.width > 0 && pageViewport && children
          ? React.Children.map(children, (child) =>
              child ? React.cloneElement(child, {
                widthPx: size.width,
                heightPx: size.height,
                viewport: pageViewport,
                displayZoom: 1,  // raster is already at native scale, no need to back out
              }) : null)
          : null}
      </div>
      {error ? (
        <div className="absolute inset-0 flex items-center justify-center bg-red-50 text-red-800 text-sm p-4 rounded">
          PDF render error: {error}
        </div>
      ) : null}
    </div>
  );
}
